import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropdown } from '../../../../hooks/useDropdown';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import SidebarHeader from './SidebarHeader';
import ConversationsList from './ConversationsList';
import ConversationDropdown from './ConversationDropdown';
import BottomSheet from './BottomSheet';
import SidebarFooter from './SidebarFooter';

/**
 * Hook para detectar si estamos en mobile
 * Con listener de resize para actualizaciones dinámicas
 */
const useIsMobile = (breakpoint = 768) => {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= breakpoint);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [breakpoint]);

    return isMobile;
};

const Sidebar = ({
    isOpen,
    themeMode,
    toggleTheme,
    startNewChat,
    conversations,
    activeConversationId,
    loadConversation,
    deleteConversation,
    user,
    token,
    handleLogout,
    handleLogin,
    handleGoSubscriptions
}) => {
    // ==================== ESTADO LOCAL ====================
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [deletingTimestamp, setDeletingTimestamp] = useState(null);

    // ==================== CUSTOM HOOKS ====================
    const isMobile = useIsMobile(768); // ✅ Detección responsive

    const {
        activeId: activeDropdown,
        position: dropdownPosition,
        toggle: toggleDropdown,
        close: closeDropdown,
        startTimeout,
        clearActiveTimeout
    } = useDropdown();

    // ==================== REFS ====================
    const dropdownRef = useRef(null);
    const bottomSheetRef = useRef(null);

    const sidebarRef = useClickOutside(
        useCallback(() => {
            if (activeDropdown) {
                closeDropdown();
                setDeletingTimestamp(null);
            }
        }, [activeDropdown, closeDropdown]),
        isMobile ? [bottomSheetRef] : [dropdownRef] // ✅ Excluir según el modo
    );

    const userMenuRef = useClickOutside(
        useCallback(() => {
            if (isUserMenuOpen) {
                setIsUserMenuOpen(false);
            }
        }, [isUserMenuOpen]),
        []
    );

    // ==================== HANDLERS ====================
    const handleMenuClick = useCallback((e, timestamp) => {
        e.stopPropagation();
        toggleDropdown(timestamp, e.currentTarget);
    }, [toggleDropdown]);

    const handleDeleteClick = useCallback((e, timestamp) => {
        e.stopPropagation();

        if (deletingTimestamp === timestamp) {
            // 2do click: Confirmar eliminación
            clearActiveTimeout();
            deleteConversation(timestamp);
            setDeletingTimestamp(null);
            closeDropdown();
        } else {
            // 1er click: Mostrar confirmación
            setDeletingTimestamp(timestamp);
            startTimeout(() => {
                setDeletingTimestamp(null);
            }, 3000);
        }
    }, [deletingTimestamp, deleteConversation, closeDropdown, startTimeout, clearActiveTimeout]);

    const handleToggleUserMenu = useCallback(() => {
        setIsUserMenuOpen(prev => !prev);
    }, []);

    // ✅ Cerrar dropdown/bottom sheet al cambiar de modo mobile/desktop
    useEffect(() => {
        if (activeDropdown) {
            closeDropdown();
            setDeletingTimestamp(null);
        }
    }, [isMobile]); // Dependencia de isMobile

    // ==================== RENDER ====================
    return (
        <div
            className={`sidebar ${isOpen ? 'open' : ''}`}
            ref={sidebarRef}
        >
            <SidebarHeader
                themeMode={themeMode}
                onToggleTheme={toggleTheme}
            />

            <ConversationsList
                conversations={conversations}
                activeConversationId={activeConversationId}
                activeDropdownId={activeDropdown}
                onNewChat={startNewChat}
                onSelectConversation={loadConversation}
                onMenuClick={handleMenuClick}
            />

            {/* ✅ RENDERIZADO CONDICIONAL: Bottom Sheet en mobile, Dropdown en desktop */}
            {isMobile ? (
                <BottomSheet
                    ref={bottomSheetRef}
                    isOpen={!!activeDropdown}
                    timestamp={activeDropdown}
                    isDeleting={deletingTimestamp === activeDropdown}
                    onDelete={handleDeleteClick}
                    onClose={closeDropdown}
                />
            ) : (
                <ConversationDropdown
                    ref={dropdownRef}
                    isVisible={!!activeDropdown}
                    position={dropdownPosition}
                    timestamp={activeDropdown}
                    themeMode={themeMode}
                    isDeleting={deletingTimestamp === activeDropdown}
                    onDelete={handleDeleteClick}
                />
            )}

            <div ref={userMenuRef}>
                <SidebarFooter
                    user={user}
                    token={token}
                    isUserMenuOpen={isUserMenuOpen}
                    onToggleUserMenu={handleToggleUserMenu}
                    onLogin={handleLogin}
                    onLogout={handleLogout}
                    onGoSubscriptions={handleGoSubscriptions}
                />
            </div>
        </div>
    );
};

export default React.memo(Sidebar);